import React, { useState, useEffect } from 'react';
import { devError } from '../../lib/devLog';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, X, Upload, Save, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { withAdminAuth } from '../../lib/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistsManager = () => {
  const { token } = useAuth();
  const [artists, setArtists] = useState([]);
  const [filteredArtists, setFilteredArtists] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    biography: '',
    imageUrl: '',
    nationality: '',
    birthYear: '',
    featured: false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchArtists();
  }, []);

  // Auto-open the create form when navigated with ?new=1
  const _location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(_location.search);
    if (params.get('new') === '1') {
      setEditingArtist(null);
      setShowForm(true);
    }
  }, [_location.search]);

  useEffect(() => {
    // Filter artists based on search
    if (searchQuery.trim()) {
      const filtered = artists.filter(artist =>
        artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        artist.nationality?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredArtists(filtered);
    } else {
      setFilteredArtists(artists);
    }
  }, [searchQuery, artists]);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/artists?include_hidden=1`);
      setArtists(response.data);
      setFilteredArtists(response.data);
      setLoading(false);
    } catch (error) {
      devError('Error fetching artists:', error);
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let imageUrl = formData.imageUrl;
      
      // Upload image if new file selected
      if (imageFile) {
        const formDataImg = new FormData();
        formDataImg.append('files', imageFile);
        
        const uploadResponse = await axios.post(
          `${API_URL}/api/admin/media/upload-multiple`,
          formDataImg,
          withAdminAuth({ headers: {
              'Content-Type': 'multipart/form-data',
            } })
        );
        
        if (uploadResponse.data.files && uploadResponse.data.files[0]) {
          imageUrl = uploadResponse.data.files[0].url;
        }
      }

      const artistData = {
        ...formData,
        imageUrl
      };

      if (editingArtist) {
        // Update artist
        await axios.put(
          `${API_URL}/api/admin/artists/${editingArtist.id}`,
          artistData,
          withAdminAuth()
        );
        alert('Artist updated successfully!');
      } else {
        // Create new artist
        await axios.post(
          `${API_URL}/api/admin/artists`,
          artistData,
          withAdminAuth()
        );
        alert('Artist created successfully!');
      }

      // Reset form and refresh list
      handleCloseForm();
      fetchArtists();
    } catch (error) {
      devError('Error saving artist:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (error.message || 'Unknown error');
      alert(`Error saving artist: ${msg}`);
    }
  };

  const handleEdit = (artist) => {
    setEditingArtist(artist);
    setFormData({
      name: artist.name || '',
      firstName: artist.firstName || '',
      lastName: artist.lastName || '',
      biography: artist.biography || '',
      imageUrl: artist.imageUrl || '',
      nationality: artist.nationality || '',
      birthYear: artist.birthYear || '',
      featured: artist.featured || false
    });
    setImagePreview(artist.imageUrl);
    setShowForm(true);
  };

  const handleDelete = async (artistId) => {
    const artist = artists.find((a) => a.id === artistId);
    const artistName = artist?.name || 'this artist';
    // Count this artist's artworks so the admin understands the cascade impact
    let artworkCount = 0;
    try {
      const r = await axios.get(`${API_URL}/api/artworks?include_hidden=1&limit=2000`);
      const list = Array.isArray(r.data) ? r.data : (r.data?.artworks || []);
      artworkCount = list.filter(
        (w) => w.artistId === artistId || w.artistName === artist?.name
      ).length;
    } catch (err) {
      // Counter is informational; deletion still proceeds with default message.
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[artists] artwork-count precheck failed:', err?.message);
      }
    }

    const msg = artworkCount > 0
      ? `Delete ${artistName}?\n\nThis will ALSO permanently delete ${artworkCount} artwork${artworkCount > 1 ? 's' : ''} attributed to them.\n\nThis cannot be undone.`
      : `Delete ${artistName}?\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;

    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/artists/${artistId}`,
        withAdminAuth()
      );
      const removed = res.data?.artworks_deleted ?? 0;
      alert(removed > 0
        ? `${artistName} deleted, along with ${removed} artwork${removed > 1 ? 's' : ''}.`
        : `${artistName} deleted.`);
      fetchArtists();
    } catch (error) {
      devError('Error deleting artist:', error);
      alert(error.response?.data?.detail || 'Error deleting artist. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(
      `Delete ${selectedArtists.length} selected artist${selectedArtists.length > 1 ? 's' : ''}?\n\nALL artworks attributed to them will also be deleted. This cannot be undone.`
    )) return;

    try {
      const results = await Promise.all(
        selectedArtists.map(id =>
          axios.delete(`${API_URL}/api/admin/artists/${id}`, withAdminAuth())
        )
      );
      const totalArtworks = results.reduce((n, r) => n + (r.data?.artworks_deleted || 0), 0);
      alert(`Deleted ${selectedArtists.length} artist${selectedArtists.length > 1 ? 's' : ''}` +
            (totalArtworks > 0 ? ` and ${totalArtworks} attributed artwork${totalArtworks > 1 ? 's' : ''}.` : '.'));
      setSelectedArtists([]);
      fetchArtists();
    } catch (error) {
      devError('Error bulk deleting artists:', error);
      alert('Error deleting artists. Please try again.');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingArtist(null);
    setFormData({
      name: '',
      firstName: '',
      lastName: '',
      biography: '',
      imageUrl: '',
      nationality: '',
      birthYear: '',
      featured: false
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const toggleSelectArtist = (artistId) => {
    if (selectedArtists.includes(artistId)) {
      setSelectedArtists(selectedArtists.filter(id => id !== artistId));
    } else {
      setSelectedArtists([...selectedArtists, artistId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedArtists.length === filteredArtists.length) {
      setSelectedArtists([]);
    } else {
      setSelectedArtists(filteredArtists.map(a => a.id));
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-light mb-2">Artists</h1>
          <p className="text-gray-600">Manage your gallery artists</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Artist
        </button>
      </div>

      {/* Search and Bulk Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search artists by name or nationality..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {selectedArtists.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedArtists.length})
            </button>
          )}
        </div>
      </div>

      {/* Artists Table */}
      {loading ? (
        <div className="text-center py-12">Loading artists...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedArtists.length === filteredArtists.length && filteredArtists.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Image</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Nationality</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Birth Year</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Featured</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredArtists.map((artist) => (
                <tr key={artist.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedArtists.includes(artist.id)}
                      onChange={() => toggleSelectArtist(artist.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <img
                      src={artist.imageUrl || '/placeholder-artist.jpg'}
                      alt={artist.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{artist.name}</td>
                  <td className="px-4 py-3 text-gray-600">{artist.nationality || 'N/A'}</td>
                  <td className="px-4 py-3 text-gray-600">{artist.birthYear || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      artist.featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {artist.featured ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(artist)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(artist.id)}
                        className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredArtists.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {searchQuery ? 'No artists found matching your search' : 'No artists yet'}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={handleCloseForm}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Form Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-2xl font-light">
                  {editingArtist ? 'Edit Artist' : 'Add New Artist'}
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Image Upload */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Profile Image</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      {imagePreview ? (
                        <div className="relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-32 h-32 rounded-full object-cover mx-auto"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setImagePreview(null);
                              setImageFile(null);
                              setFormData({ ...formData, imageUrl: '' });
                            }}
                            className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600 mb-2">Upload artist profile photo</p>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Full Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="e.g., Andy Warhol"
                    />
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>

                  {/* Nationality */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Nationality</label>
                    <input
                      type="text"
                      value={formData.nationality}
                      onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="e.g., American"
                    />
                  </div>

                  {/* Birth Year */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Birth Year</label>
                    <input
                      type="number"
                      value={formData.birthYear}
                      onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="e.g., 1928"
                    />
                  </div>

                  {/* Biography */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Biography</label>
                    <textarea
                      rows={6}
                      value={formData.biography}
                      onChange={(e) => setFormData({ ...formData, biography: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder="Artist biography..."
                    />
                  </div>

                  {/* Featured */}
                  <div className="md:col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="featured" className="text-sm font-medium">
                      Featured Artist
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    {editingArtist ? 'Update Artist' : 'Create Artist'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtistsManager;
